import React from "react";
import { toast } from "react-toastify";

import CopyLink from "../../../assets/svg/CopyIcon.svg";
import { remittanceApi } from "../../remittance-beta/api/remittanceApi";
import { InlineSpinner } from "../../remittance-beta/components/modal/DigioEsignModal/InlineSpinner";
import {
  formatAdIIProfileStatusLabel,
  getAdIIApiError,
  getAdIIKycShareLink,
  isAdIIKycInProgressStatus,
  isAdIIKycRejected,
  shouldShowAdIIGenerateNewLink,
} from "../../remittance-beta/utils/remitterKycFlow";
import { logApiErrorToSentry } from "../../remittance-beta/utils";
import { formatQuoteDateTime } from "../../remittance-beta/utils/avanseQuote";

type AdIILoadingAction = "resend" | "generate" | "copy" | null;

export type AdIIHistoryVkyc = {
  canResend?: boolean;
  canCopyLink?: boolean;
  canGenerateNewLink?: boolean;
  portalLink?: string | null;
  captureLink?: string | null;
  profileStatus?: string;
  profileReviewerAction?: string | null;
  profileReviewerStatus?: string | null;
  completed?: boolean;
  completedOn?: string | null;
  uniqueKey?: string;
  userKey?: string;
  userId?: string | number;
  expired?: boolean;
  status?: string;
};

interface AdIIHistoryKycActionsProps {
  vkyc?: AdIIHistoryVkyc | null;
  onVkycUpdate?: (vkyc: AdIIHistoryVkyc) => void;
}

interface AdIIHistoryKycActionsProps {
  vkyc?: AdIIHistoryVkyc | null;
  onVkycUpdate?: (vkyc: AdIIHistoryVkyc) => void;
}

const AdIIHistoryKycActions: React.FC<AdIIHistoryKycActionsProps> = ({
  vkyc,
  onVkycUpdate,
}) => {
  const [loadingAction, setLoadingAction] = React.useState<AdIILoadingAction>(null);
  const [isLinkCopied, setIsLinkCopied] = React.useState(false);

  if (!vkyc) return null;

  const isLoading = Boolean(loadingAction);
  const copyLink = getAdIIKycShareLink(vkyc);
  const reviewerApproved = [vkyc.profileReviewerAction, vkyc.profileReviewerStatus]
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
    .map((value) => String(value).toLowerCase())
    .includes("approved");
  const profileStatus = String(vkyc.profileStatus || "").toLowerCase();
  const showCompleted =
    (Boolean(vkyc.completed) || (profileStatus === "completed" && reviewerApproved)) &&
    !isAdIIKycRejected(vkyc);

  if (showCompleted) {
    return (
      <span className="text-[#37C820] font-semibold">
        Completed
        {vkyc.completedOn ? ` · ${formatQuoteDateTime(vkyc.completedOn)}` : ""}
      </span>
    );
  }

  const showGenerate = shouldShowAdIIGenerateNewLink(vkyc);
  const showResend = !showGenerate && Boolean(vkyc.canResend);
  const showCopy = !showGenerate && Boolean(vkyc.canCopyLink) && Boolean(copyLink);

  const handleCopy = () => {
    if (!copyLink || isLoading) return;
    setLoadingAction("copy");
    navigator.clipboard.writeText(copyLink);
    setIsLinkCopied(true);
    toast.success("KYC link copied");
    setTimeout(() => {
      setIsLinkCopied(false);
      setLoadingAction(null);
    }, 2000);
  };

  const handleResend = async () => {
    if (!vkyc.userKey || isLoading) return;
    setLoadingAction("resend");
    try {
      const response = await remittanceApi.resendAdIIVkycLink({
        userKey: vkyc.userKey,
        uniqueKey: vkyc.uniqueKey,
        userId: vkyc.userId != null ? String(vkyc.userId) : undefined,
      });
      onVkycUpdate?.(response?.data ?? vkyc);
      toast.success("KYC link resent successfully");
    } catch (err: any) {
      toast.error(getAdIIApiError(err, "Unable to resend KYC link"));
      logApiErrorToSentry(err, "historyAdIIResendKycLink");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleGenerate = async () => {
    if (!vkyc.uniqueKey || isLoading) return;
    setLoadingAction("generate");
    try {
      const response = await remittanceApi.generateAdIIVkycNewLink({
        uniqueKey: vkyc.uniqueKey,
        sendType: "both",
      });
      onVkycUpdate?.(response?.data ?? vkyc);
      toast.success("New KYC link generated and sent");
    } catch (err: any) {
      toast.error(getAdIIApiError(err, "Unable to generate new KYC link"));
      logApiErrorToSentry(err, "historyAdIIGenerateNewLink");
    } finally {
      setLoadingAction(null);
    }
  };

  if (!showGenerate && !showResend && !showCopy) {
    return (
      <span className="text-[#64748B]">
        {isAdIIKycInProgressStatus(vkyc)
          ? formatAdIIProfileStatusLabel(vkyc)
          : vkyc.status || "—"}
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {showResend && (
        <button
          type="button"
          onClick={handleResend}
          disabled={isLoading}
          className="text-[#0F5BF1] font-semibold disabled:opacity-60"
        >
          {loadingAction === "resend" ? <InlineSpinner /> : "Resend KYC Link"}
        </button>
      )}
      {showGenerate && (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isLoading || !vkyc.uniqueKey}
          className="text-[#0F5BF1] font-semibold disabled:opacity-60"
        >
          {loadingAction === "generate" ? <InlineSpinner /> : "Generate New Link"}
        </button>
      )}
      {showCopy && (
        <button
          type="button"
          onClick={handleCopy}
          disabled={isLoading}
          className="flex items-center gap-1 disabled:opacity-60"
        >
          <span
            className={`text-xs font-semibold ${
              isLinkCopied ? "text-green-500" : "text-[#0F5BF1]"
            }`}
          >
            {isLinkCopied ? "Link Copied" : "Copy Link"}
          </span>
          <img className="pl-1" src={CopyLink} alt="copy-link" />
        </button>
      )}
    </div>
  );
};

export default AdIIHistoryKycActions;
